import { injectEntityList } from '../../../../../../common/store/base/injectEntityList';
import { injectPrimitive } from '../../../../../../common/store/base/injectPrimitive';
import { inject } from '../../../../../../common/container/inject';
import { IEntityListStore } from '../../../../../../common/store/interface/IEntityListStore';
import { IPrimitiveStore } from '../../../../../../common/store/interface/IPrimitiveStore';
import { ILocalization, ILocalizationToken } from '../../../../../../application/language/ILocalization';
import { ApplicationRoutes } from '../../../../../../application/constants/routes';
import { IUserEntityModel } from '../../../../../../service/user/IUserEntityModel';
import { ILearningUserDetailModel } from '../../../../../../service/report/interface/ILearningUserDetailModel';
import { IAnalyticsPageHeaderBreadcrumb } from '../../main/parts/AnalyticsPageHeader';
import { CREATE_ANALYTICS_ROOT_BREADCRUMB } from '../../main/parts/analyticsHeaderBreadcrumbs';
import { IAnalyticsUserProgressSummary, IAnalyticsUserUnitCard } from './AnalyticsUserDTO';
import { IAnalyticsUserDomain } from './IAnalyticsUserDomain';
import { IAnalyticsUserUI } from './IAnalyticsUserUI';

const DEFAULT_PROGRESS_SUMMARY: IAnalyticsUserProgressSummary = {
  earnedImportantScore: 0,
  importantMaxScore: 0,
  progressPercent: 0,
};

const statusWeight = {
  completed: 3,
  failed: 2,
  inProgress: 1,
  notStarted: 0,
} as const;

const sortUnits = (units: IAnalyticsUserUnitCard[]) =>
  [...units].sort((a, b) => {
    const statusDiff = statusWeight[b.status] - statusWeight[a.status];
    if (statusDiff !== 0) {
      return statusDiff;
    }

    const progressDiff = b.progress - a.progress;
    if (progressDiff !== 0) {
      return progressDiff;
    }

    return b.earned - a.earned;
  });

export class AnalyticsUserUI implements IAnalyticsUserUI {
  isFetchingUser: IPrimitiveStore<boolean>;
  user: IPrimitiveStore<IUserEntityModel | null>;
  unitCards: IEntityListStore<IAnalyticsUserUnitCard>;
  progressSummary: IPrimitiveStore<IAnalyticsUserProgressSummary>;
  breadcrumbs: IPrimitiveStore<IAnalyticsPageHeaderBreadcrumb[]>;
  userDetail: IPrimitiveStore<ILearningUserDetailModel | null>;

  constructor(
    private analyticsUserDomain: IAnalyticsUserDomain,
    private userId?: string,
    private i18n = inject<ILocalization>(ILocalizationToken),
  ) {
    this.isFetchingUser = injectPrimitive(Boolean(userId));
    this.user = injectPrimitive<IUserEntityModel | null>(null);
    this.unitCards = injectEntityList<IAnalyticsUserUnitCard>([]);
    this.progressSummary = injectPrimitive<IAnalyticsUserProgressSummary>(DEFAULT_PROGRESS_SUMMARY);
    this.breadcrumbs = injectPrimitive<IAnalyticsPageHeaderBreadcrumb[]>([
      CREATE_ANALYTICS_ROOT_BREADCRUMB(this.i18n.translate('analytics.title')),
    ]);
    this.userDetail = injectPrimitive<ILearningUserDetailModel | null>(null);
  }

  private resetState() {
    this.user.setValue(null);
    this.userDetail.setValue(null);
    this.unitCards.setList([]);
    this.progressSummary.setValue(DEFAULT_PROGRESS_SUMMARY);
    this.breadcrumbs.setValue([
      CREATE_ANALYTICS_ROOT_BREADCRUMB(this.i18n.translate('analytics.title')),
    ]);
  }

  boot = async () => {
    if (!this.userId) {
      this.resetState();
      this.isFetchingUser.setValue(false);
      return;
    }

    this.isFetchingUser.setValue(true);
    try {
      const [user, detail] = await Promise.all([
        this.analyticsUserDomain.getUserById(this.userId),
        this.analyticsUserDomain.getUserDetail(this.userId),
      ]);

      this.user.setValue(user);
      this.userDetail.setValue(detail ?? null);
      const progressSummary = detail?.progressSummary ?? DEFAULT_PROGRESS_SUMMARY;
      const units = detail?.units ?? [];

      this.progressSummary.setValue(progressSummary);
      this.unitCards.setList(sortUnits(units));
      this.breadcrumbs.setValue([
        CREATE_ANALYTICS_ROOT_BREADCRUMB(this.i18n.translate('analytics.title')),
        {
          label: detail?.displayName || user.displayName || this.userId,
          link: ApplicationRoutes.AnalyticsUser.replace(':userId', this.userId),
        },
      ]);
    } finally {
      this.isFetchingUser.setValue(false);
    }
  };
}
